import { useMemo } from "react";
import { PMBadge, PMSkeleton } from "@/ds/vendor";
import { useApp } from "@/app/AppContext";
import { fetchForecast, fetchGdd, sky, sprayWindows, tempU, windU, rainU, useAsync, type Forecast } from "@/data/weather";
import { clock } from "./format";

const dow = (d: string) => new Date(d + "T12:00:00").toLocaleDateString(undefined, { weekday: "short" });
const seasonStart = () => `${new Date().getFullYear()}-04-01`;

function SprayBadge({ f }: { f: Forecast }) {
  const w = sprayWindows(f)[0];
  if (!w) return <PMBadge variant="error" dot>No spray window 48h</PMBadge>;
  const today = new Date(w.start).toDateString() === new Date().toDateString();
  return <PMBadge variant="success" dot>Spray {today ? "" : new Date(w.start).toLocaleDateString(undefined, { weekday: "short" }) + " "}{clock(w.start)}–{clock(new Date(new Date(w.end).getTime() + 3_600_000).toISOString())}</PMBadge>;
}

// Full conditions panel for one location: now, spray window, 7 days, season GDD.
export function ConditionsPanel({ lat, lng }: { lat: number; lng: number }) {
  const { units } = useApp();
  const f = useAsync(() => fetchForecast(lat, lng, units), [lat, lng, units]);
  const g = useAsync(() => fetchGdd(lat, lng, seasonStart(), units), [lat, lng, units]);
  const max = useMemo(() => (f.data ? Math.max(...f.data.days.map((d) => d.tMax)) : 1), [f.data]);
  const min = useMemo(() => (f.data ? Math.min(...f.data.days.map((d) => d.tMin)) : 0), [f.data]);
  if (f.error) return <p className="hint">Conditions unavailable offline.</p>;
  if (!f.data) return <PMSkeleton shape="rect" height={180} />;
  const s = sky(f.data.now.code);
  return (
    <div className="cond">
      <div className="cond-now">
        <span className="cond-sky">{s.emoji}</span>
        <div>
          <div className="cond-temp">{Math.round(f.data.now.temp)}{tempU(units)}</div>
          <div className="meta">{s.label}<span className="sep" />Wind {Math.round(f.data.now.wind)} {windU(units)}</div>
        </div>
        <span className="spacer" />
        <SprayBadge f={f.data} />
      </div>
      <div className="cond-week" role="list" aria-label="7-day forecast">
        {f.data.days.map((d, i) => (
          <div className="cond-day" role="listitem" key={d.date} title={`${sky(d.code).label}, rain ${d.rainProb}%`}>
            <span className="cond-dow">{i === 0 ? "Today" : dow(d.date)}</span>
            <span className="cond-emoji">{sky(d.code).emoji}</span>
            <span className="cond-bar"><i style={{ bottom: `${((d.tMin - min) / (max - min || 1)) * 100}%`, top: `${100 - ((d.tMax - min) / (max - min || 1)) * 100}%` }} /></span>
            <span className="cond-hi">{Math.round(d.tMax)}°</span>
            <span className="cond-lo">{Math.round(d.tMin)}°</span>
            <span className={`cond-rain ${d.rainProb >= 50 ? "is-wet" : ""}`}>💧{d.rainProb}%</span>
          </div>
        ))}
      </div>
      <div className="cond-season">
        <div><span className="stat-lbl">GDD since Apr 1</span><strong>{g.data ? g.data.gdd.toLocaleString() : "—"}</strong><small>base {units === "us" ? "50°F" : "10°C"}</small></div>
        <div><span className="stat-lbl">Season rain</span><strong>{g.data ? `${g.data.rain} ${rainU(units)}` : "—"}</strong><small>{g.data ? `${g.data.days} days` : ""}</small></div>
        <div><span className="stat-lbl">Rain next 7d</span><strong>{Math.round(f.data.days.reduce((a, d) => a + d.rainSum, 0) * 100) / 100} {rainU(units)}</strong><small>forecast</small></div>
      </div>
    </div>
  );
}

// Compact row for lists of farms (Home widget).
export function ConditionsRow({ label, emoji, lat, lng, onOpen }: { label: string; emoji: string; lat: number; lng: number; onOpen?: () => void }) {
  const { units } = useApp();
  const f = useAsync(() => fetchForecast(lat, lng, units), [lat, lng, units]);
  const today = f.data?.days[0];
  return (
    <button className="cond-row" onClick={onOpen}>
      <span className="emoji-disc">{emoji}</span>
      <span className="list-main"><span className="list-title">{label}</span>{today ? <span className="meta">{sky(today.code).label}<span className="sep" />{Math.round(today.tMax)}° / {Math.round(today.tMin)}°<span className="sep" />💧{today.rainProb}%<span className="sep" />{Math.round(today.windMax)} {windU(units)}</span> : <span className="meta">{f.error ? "Unavailable" : "Loading…"}</span>}</span>
      {today && <span className="cond-row-sky">{sky(today.code).emoji}</span>}
      {f.data && <SprayBadge f={f.data} />}
    </button>
  );
}

// Small inline chip: current conditions at a point (header, map card).
export function WeatherChip({ lat, lng }: { lat: number; lng: number }) {
  const { units } = useApp();
  const f = useAsync(() => fetchForecast(lat, lng, units), [lat, lng, units]);
  if (!f.data) return null;
  const s = sky(f.data.now.code);
  return <span className="wx-chip" title={s.label}>{s.emoji} {Math.round(f.data.now.temp)}{tempU(units)}<span className="sep" />{Math.round(f.data.now.wind)} {windU(units)}</span>;
}
